import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useBlockEnvironment } from '@zillow/cms-blocks/BlockEnvironment';
import { ModalDialog, Box, Heading, Grid, IconUser, IconUserOutline, IconHouse, IconHouseOutline, IconTeam, IconTeamOutline, Button, IconArrowRightOutline, Paragraph, Anchor, IconCheckmarkCircleOutline, IconNewTeamMemberOutline, IconDownloadOutline, IconSchoolOutline, Alert, IconBulbOutline, TextButton } from '@zillow/constellation';
import { event } from '@zillow/universal-analytics';
import InputMask from 'inputmask';
import React, { useState, useEffect } from 'react';
import { useCurrentUser } from '../../hooks/user.js';
import { UncontrolledInputField, UncontrolledFieldSet } from '../UncontrolledFields/index.js';
import ArrowBlockButton from './ArrowBlockButton.js';
import FormStepper, { INVISIBLE_SX } from './FormStepper.js';
import OverlappingStack from './OverlappingStack.js';
import { usePAFormStepperFieldRefs } from './PAFormStepperModalHooks.js';
import Tile from './Tile.js';
import { trackModalDialog, trackStepImpression, trackTileSelect, trackTileImpression, trackInputChange, trackArrowButtonClick, trackBack, trackTileLink, trackContinue } from './clickstream.js';
import { getNameError, getEmailError, getPhoneError, getOrgError } from './utils.js';

function PAFormStepperModal() {
  var _useBlockEnvironment = useBlockEnvironment(),
    environment = _useBlockEnvironment.environment;
  var _useState = useState('welcome'),
    _useState2 = _slicedToArray(_useState, 2),
    visibleStepName = _useState2[0],
    setVisibleStepName = _useState2[1];
  var _useState3 = useState('agent'),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedWelcomeTile = _useState4[0],
    setSelectedWelcomeTile = _useState4[1];
  var homeLinks = [{
    label: 'Search for a home',
    href: 'https://www.zillow.com/'
  }, {
    label: 'List your home for free',
    href: 'https://www.zillow.com/for-sale-by-owner/'
  }, {
    label: "See your home's estimated value",
    href: 'https://www.zillow.com/how-much-is-my-home-worth/'
  }, {
    label: 'Find a real estate agent',
    href: 'https://www.zillow.com/professionals/real-estate-agent-reviews/'
  }, {
    label: 'Help',
    href: 'https://zillow.zendesk.com/hc/en-us/'
  }];
  var otherLinks = [{
    label: "I'm a lender or loan officer",
    href: 'https://www.zillow.com/advertising/mortgage/'
  }, {
    label: "I'm a property manager",
    href: 'https://www.zillow.com/rentals-network/rental-advertising/'
  }, {
    label: "I'm a brand advertiser",
    href: 'https://www.zillow.com/brands/'
  }, {
    label: 'Help',
    href: 'https://zillow.zendesk.com/hc/en-us/'
  }];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    submitting = _useState6[0],
    setSubmitting = _useState6[1];
  var _usePAFormStepperFiel = usePAFormStepperFieldRefs(),
    nameField = _usePAFormStepperFiel.nameField,
    emailField = _usePAFormStepperFiel.emailField,
    phoneField = _usePAFormStepperFiel.phoneField,
    orgField = _usePAFormStepperFiel.orgField;
  var _useState7 = useState({
      name: '',
      email: '',
      phone: ''
    }),
    _useState8 = _slicedToArray(_useState7, 2),
    defaultValues = _useState8[0],
    setDefaultValues = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    isOpen = _useState10[0],
    setIsOpen = _useState10[1];
  var _useCurrentUser = useCurrentUser(environment === 'prod' ? 'production' : 'qa'),
    user = _useCurrentUser.data.user;
  useEffect(function () {
    var _user$profile, _nameField$current, _emailField$current, _phoneField$current;
    if (!user || typeof user === 'boolean') {
      return;
    }
    var nextDefaultValues = {
      name: user.firstName + ' ' + (user === null || user === void 0 ? void 0 : user.lastName),
      email: user.email || '',
      phone: ((_user$profile = user.profile) === null || _user$profile === void 0 ? void 0 : _user$profile.cellNumber) || ''
    };
    (_nameField$current = nameField.current) === null || _nameField$current === void 0 || _nameField$current.setValue(nextDefaultValues.name);
    (_emailField$current = emailField.current) === null || _emailField$current === void 0 || _emailField$current.setValue(nextDefaultValues.email);
    (_phoneField$current = phoneField.current) === null || _phoneField$current === void 0 || _phoneField$current.setValue(nextDefaultValues.phone);
    setDefaultValues(nextDefaultValues);
  }, [emailField, nameField, phoneField, user]);

  // Return true if "should submit"
  function goToNextStepOrSubmit() {
    var _nameField$current2, _emailField$current2, _emailField$current3, _phoneField$current2, _phoneField$current3, _orgField$current;
    switch (visibleStepName) {
      case 'welcome':
        if ((_nameField$current2 = nameField.current) !== null && _nameField$current2 !== void 0 && _nameField$current2.updateAndGetError()) {
          var _nameField$current3;
          (_nameField$current3 = nameField.current) === null || _nameField$current3 === void 0 || _nameField$current3.focus();
          return;
        }
        trackContinue('welcome', 'agent-email');
        handleStepChange('agent-email');
        (_emailField$current2 = emailField.current) === null || _emailField$current2 === void 0 || _emailField$current2.focus();
        return;
      case 'agent-email':
        if ((_emailField$current3 = emailField.current) !== null && _emailField$current3 !== void 0 && _emailField$current3.updateAndGetError()) {
          var _emailField$current4;
          (_emailField$current4 = emailField.current) === null || _emailField$current4 === void 0 || _emailField$current4.focus();
          return;
        }
        trackContinue('agent-email', 'agent-phone');
        handleStepChange('agent-phone');
        (_phoneField$current2 = phoneField.current) === null || _phoneField$current2 === void 0 || _phoneField$current2.focus();
        return;
      case 'agent-phone':
        if ((_phoneField$current3 = phoneField.current) !== null && _phoneField$current3 !== void 0 && _phoneField$current3.updateAndGetError()) {
          var _phoneField$current4;
          (_phoneField$current4 = phoneField.current) === null || _phoneField$current4 === void 0 || _phoneField$current4.focus();
          return;
        }
        trackContinue('agent-phone', 'agent-org');
        handleStepChange('agent-org');
        return;
      case 'agent-org':
        if ((_orgField$current = orgField.current) !== null && _orgField$current !== void 0 && _orgField$current.updateAndGetError()) {
          return;
        }
        return true;
      default:
        throw new Error("unhandled step: " + visibleStepName);
    }
  }
  useEffect(function addSyncOpenWithHashListenerOnMount() {
    function updateIsOpen() {
      if (window.location.hash === '#become-a-premier-agent') {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }

    // Listen using history api, if "#become-a-premier-agent" setIsOpen to
    // true, and when it no longer is in the url then setIsOpen to false.
    window.addEventListener('hashchange', updateIsOpen);

    // Update on mount
    updateIsOpen();
    return function () {
      window.removeEventListener('hashchange', updateIsOpen);
    };
  }, []);
  useEffect(function trackModalOpen() {
    if (!isOpen) {
      return;
    }
    trackModalDialog('welcome', true);
  }, [isOpen]);
  function handleStepChange(step) {
    setVisibleStepName(step);
  }
  useEffect(function trackStepImpressionOnStepDisplay() {
    if (!isOpen) {
      return;
    }
    if (selectedWelcomeTile !== 'agent') {
      return;
    }
    trackStepImpression(visibleStepName);
  }, [isOpen, visibleStepName, selectedWelcomeTile]);
  return /*#__PURE__*/React.createElement(ModalDialog, {
    size: {
      default: ModalDialog.SIZES.FULL_SCREEN,
      md: ModalDialog.SIZES.LG
    },
    scrollDocument: true,
    dividers: false,
    isOpen: isOpen,
    onClose: function onClose() {
      // Using window.location.hash causes a jump to the top of the
      // page. Push state does not, however it doesn't reliably
      // trigger the popstate event so we have to setIsOpen(false)
      // here.
      history.pushState(null, '', location.href.substring(0, location.href.indexOf('#')));
      setIsOpen(false);

      // Reset back to first step, otherwise if user re-opens modal,
      // the <form> no longer has its state, as <Modal> destroys the
      // HTML.
      setVisibleStepName('welcome');
      trackModalDialog(visibleStepName, false);
    },
    renderHeader: null,
    renderBody: /*#__PURE__*/React.createElement(FormStepper, {
      onShouldSubmit: goToNextStepOrSubmit,
      visibleStepName: visibleStepName,
      onPrepareSubmitData: function onPrepareSubmitData(data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        var nextData = Object.assign({}, data, {
          fullName: data.name.trim(),
          Name: data.name.trim(),
          Email: data.email.trim(),
          // Replace all non-numeric characters
          Phone: data.phone.replace(/\D/g, ''),
          Brokerage_Size: data.org,
          Business_Line: 'Premier Agent',
          Warm_Lead_Source: 'PA-Multistep-Form'
        });

        // Delete the keys we only use in the frontend.
        delete nextData.name;
        delete nextData.email;
        delete nextData.phone;
        delete nextData.org;
        return nextData;
      },
      onSubmitSuccess: function onSubmitSuccess() {
        handleStepChange('agent-thank-you');
        event({
          // https://dataportal.zillowgroup.net/event-registry/2048
          envelope: {
            event_type_id: '2048',
            event_type_version_id: '4',
            event_template_id: '126',
            event_template_version_id: '1'
          },
          clickstream_trigger: {
            trigger_type_nm: 'interaction',
            trigger_location_nm: 'cms',
            trigger_source_nm: 'cms_pa_multistep_form',
            trigger_object_nm: 'no_trigger_object'
          },
          semantic: {
            semantic_event_nm: 'cms_submit'
          },
          cms_info: {
            block_type_cd: 'apex_form',
            block_id: 'pa_multistep_form',
            form_business_line_txt: 'Premier Agent',
            form_warm_lead_source_txt: 'PA-Form',
            form_is_valid_ind: true,
            form_invalid_fields_txt: [],
            form_submit_success_ind: true,
            form_server_response_message_txt: ''
          }
        });
        setSubmitting(false);
      },
      onSubmitStart: function onSubmitStart() {
        return setSubmitting(true);
      },
      onSubmitError: function onSubmitError() {
        var _orgField$current2;
        (_orgField$current2 = orgField.current) === null || _orgField$current2 === void 0 || _orgField$current2.setError('Sorry, submission failed. Please try again soon.');
        event({
          // https://dataportal.zillowgroup.net/event-registry/2048
          envelope: {
            event_type_id: '2048',
            event_type_version_id: '4',
            event_template_id: '126',
            event_template_version_id: '1'
          },
          clickstream_trigger: {
            trigger_type_nm: 'interaction',
            trigger_location_nm: 'cms',
            trigger_source_nm: 'cms_pa_multistep_form',
            trigger_object_nm: 'no_trigger_object'
          },
          semantic: {
            semantic_event_nm: 'cms_submit'
          },
          cms_info: {
            block_type_cd: 'apex_form',
            block_id: 'pa_multistep_form',
            form_business_line_txt: 'Premier Agent',
            form_warm_lead_source_txt: 'PA-Multistep-Form',
            form_is_valid_ind: true,
            form_invalid_fields_txt: [],
            form_submit_success_ind: false,
            form_server_response_message_txt: ''
          }
        });
        setSubmitting(false);
      }
    }, /*#__PURE__*/React.createElement(FormStepper.Step, {
      progressBar: selectedWelcomeTile === 'agent' && {
        current: 1,
        total: 4
      },
      name: "welcome"
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Heading, {
      level: "5",
      fontFamily: "serif",
      textAlign: "center"
    }, "Who are you?"), /*#__PURE__*/React.createElement(Grid, {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: {
        default: 'xs',
        md: 'sm'
      },
      marginTop: "sm",
      marginBottom: "lg"
    }, /*#__PURE__*/React.createElement(Tile, {
      title: "Agent or broker",
      Icon: IconUser,
      IconOutline: IconUserOutline,
      selected: selectedWelcomeTile === 'agent',
      onClick: function onClick() {
        setSelectedWelcomeTile('agent');
        trackTileSelect('Agent or broker');
      }
    }), /*#__PURE__*/React.createElement(Tile, {
      title: "Home Buyer or Seller",
      Icon: IconHouse,
      IconOutline: IconHouseOutline,
      selected: selectedWelcomeTile === 'homer',
      onClick: function onClick() {
        setSelectedWelcomeTile('homer');
        trackTileImpression('homer');
        trackTileSelect('Home Buyer or Seller');
      }
    }), /*#__PURE__*/React.createElement(Tile, {
      title: "Other Professional",
      Icon: IconTeam,
      IconOutline: IconTeamOutline,
      selected: selectedWelcomeTile === 'other',
      onClick: function onClick() {
        setSelectedWelcomeTile('other');
        trackTileImpression('other');
        trackTileSelect('Other Professional');
      }
    })), /*#__PURE__*/React.createElement(OverlappingStack, null, /*#__PURE__*/React.createElement(Box, {
      sx: selectedWelcomeTile !== 'agent' && INVISIBLE_SX
    }, /*#__PURE__*/React.createElement(PAHeading, null, "What's your name?"), /*#__PURE__*/React.createElement(UncontrolledInputField, {
      placeholder: "First and last name",
      type: "text",
      name: "name",
      autoComplete: "name",
      ref: nameField,
      onBlurAfterChange: function onBlurAfterChange() {
        trackInputChange('name', 'First and last name', visibleStepName);
      },
      getError: getNameError,
      formFieldProps: {
        marginBottom: 'sm'
      },
      defaultValue: defaultValues.name
    }), /*#__PURE__*/React.createElement(Button, {
      onClick: goToNextStepOrSubmit,
      type: "button",
      buttonType: "primary",
      icon: /*#__PURE__*/React.createElement(IconArrowRightOutline, null),
      iconPosition: "end",
      fluid: true,
      marginBottom: "md"
    }, "Continue"), /*#__PURE__*/React.createElement(Paragraph, {
      textAlign: "center"
    }, "A member of our team will reach out for a business consultation or you can call us directly at", ' ', /*#__PURE__*/React.createElement(Anchor, {
      href: "tel:8558850115"
    }, "855-885-0115"), ".")), /*#__PURE__*/React.createElement(Box, {
      sx: selectedWelcomeTile !== 'homer' && INVISIBLE_SX
    }, /*#__PURE__*/React.createElement(Paragraph, {
      textAlign: "center",
      fontType: "bodySmallHeading",
      marginY: "xs"
    }, "Please explore any of the following options to continue:"), /*#__PURE__*/React.createElement(Box, null, homeLinks.map(function (link) {
      return /*#__PURE__*/React.createElement(ArrowBlockButton, {
        href: link.href,
        key: link.label,
        onClick: function onClick() {
          return trackArrowButtonClick(link, 'homer');
        },
        marginBottom: "xs"
      }, link.label);
    }))), /*#__PURE__*/React.createElement(Box, {
      sx: selectedWelcomeTile !== 'other' && INVISIBLE_SX
    }, /*#__PURE__*/React.createElement(Paragraph, {
      textAlign: "center",
      fontType: "bodySmallHeading",
      marginY: "xs"
    }, "Please explore any of the following options to continue:"), /*#__PURE__*/React.createElement(Box, null, otherLinks.map(function (link) {
      return /*#__PURE__*/React.createElement(ArrowBlockButton, {
        href: link.href,
        key: link.label,
        marginBottom: "xs",
        onClick: function onClick() {
          return trackArrowButtonClick(link, 'other');
        }
      }, link.label);
    })))))), /*#__PURE__*/React.createElement(FormStepper.Step, {
      progressBar: {
        current: 2,
        total: 4
      },
      name: "agent-email",
      onBack: function onBack() {
        trackBack(visibleStepName, 'welcome');
        handleStepChange('welcome');
      }
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(PAHeading, null, "What's your email?"), /*#__PURE__*/React.createElement(UncontrolledInputField, {
      placeholder: "Email",
      name: "email",
      autoComplete: "email",
      type: "email",
      ref: emailField,
      onBlurAfterChange: function onBlurAfterChange() {
        trackInputChange('email', 'Email', visibleStepName);
      },
      getError: getEmailError,
      formFieldProps: {
        marginBottom: 'xs'
      },
      defaultValue: defaultValues.email
    }), /*#__PURE__*/React.createElement(Button, {
      type: "button",
      onClick: goToNextStepOrSubmit,
      buttonType: "primary",
      icon: /*#__PURE__*/React.createElement(IconArrowRightOutline, null),
      iconPosition: "end",
      fluid: true,
      marginBottom: "lg"
    }, "Continue"), /*#__PURE__*/React.createElement(MobileBackButton, {
      onClick: function onClick() {
        return handleStepChange('welcome');
      }
    }), /*#__PURE__*/React.createElement(PATipAlert, {
      message: "1M actual buyers sought a Zillow Premier Agent in 2023 - that's nearly a third of all home buyers."
    }))), /*#__PURE__*/React.createElement(FormStepper.Step, {
      progressBar: {
        current: 3,
        total: 4
      },
      name: "agent-phone",
      onBack: function onBack() {
        trackBack(visibleStepName, 'agent-email');
        handleStepChange('agent-email');
      }
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(PAHeading, null, "What's your phone number?"), /*#__PURE__*/React.createElement(UncontrolledInputField, {
      placeholder: "Phone",
      type: "tel",
      name: "phone",
      autoComplete: "tel",
      ref: phoneField,
      onBlurAfterChange: function onBlurAfterChange() {
        trackInputChange('phone', 'Phone', visibleStepName);
      },
      getError: getPhoneError,
      formFieldProps: {
        marginBottom: 'xs'
      },
      defaultValue: defaultValues.phone,
      onMount: function onMount(inputRef) {
        if (!inputRef.current) {
          return;
        }
        var masker = new InputMask({
          // Phone without extension
          mask: '(999) 999-9999',
          placeholder: '_',
          autoUnmask: true
        });
        masker.mask(inputRef.current);
      }
    }), /*#__PURE__*/React.createElement(Button, {
      type: "button",
      onClick: goToNextStepOrSubmit,
      buttonType: "primary",
      icon: /*#__PURE__*/React.createElement(IconArrowRightOutline, null),
      iconPosition: "end",
      fluid: true,
      marginBottom: "lg"
    }, "Continue"), /*#__PURE__*/React.createElement(MobileBackButton, {
      onClick: function onClick() {
        return handleStepChange('agent-email');
      }
    }), /*#__PURE__*/React.createElement(PATipAlert, {
      message: "Every 4 seconds, Zillow connects a home buyer with a Zillow Premier Agent."
    }))), /*#__PURE__*/React.createElement(FormStepper.Step, {
      progressBar: {
        current: 4,
        total: 4
      },
      name: "agent-org",
      onBack: function onBack() {
        trackBack(visibleStepName, 'agent-phone');
        handleStepChange('agent-phone');
      }
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(PAHeading, null, "What's your organization size?"), /*#__PURE__*/React.createElement(Box, {
      sx: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '$spacing.lg'
      }
    }, /*#__PURE__*/React.createElement(UncontrolledFieldSet, {
      name: "org",
      onChange: function onChange() {
        trackInputChange('org', "What's your organization size?", visibleStepName);
      },
      getError: getOrgError,
      ref: orgField,
      disabled: submitting,
      options: [{
        label: 'I am an individual agent/broker',
        value: 'I am an individual agent/broker'
      }, {
        label: 'I lead a team/brokerage of 10 or less people',
        value: 'I lead a team/brokerage of 10 or less people'
      }, {
        label: 'I lead a brokerage of 11+ people',
        value: 'I lead a brokerage of 11+ people'
      }]
    })), /*#__PURE__*/React.createElement(Button, {
      type: "submit",
      buttonType: "primary",
      disabled: submitting,
      marginBottom: "lg",
      fluid: true
    }, submitting ? 'Submitting' : 'Submit'), /*#__PURE__*/React.createElement(Paragraph, {
      textAlign: "center"
    }, "or call us directly at", ' ', /*#__PURE__*/React.createElement(Anchor, {
      href: "tel:8558850115"
    }, "855-885-0115")), /*#__PURE__*/React.createElement(MobileBackButton, {
      onClick: function onClick() {
        return handleStepChange('agent-phone');
      },
      sx: {
        marginBottom: '$spacing.lg',
        marginTop: '$spacing.lg'
      }
    }), /*#__PURE__*/React.createElement(Paragraph, {
      textAlign: "center",
      fontType: "finePrint"
    }, "By pressing Submit, you agree that Zillow Group may contact you via phone/text about your inquiry, which may involve the use of automated means. You are not required to consent as a condition of purchasing any goods or services. Message/data rates may apply."))), /*#__PURE__*/React.createElement(FormStepper.Step, {
      name: "agent-thank-you"
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Box, {
      as: Paragraph,
      textAlign: "center",
      marginBottom: "sm",
      sx: {
        '&&': {
          marginTop: 'calc(-1 * $spacing.xl)'
        }
      }
    }, /*#__PURE__*/React.createElement(IconCheckmarkCircleOutline, {
      fontColor: "brand",
      size: "lg"
    })), /*#__PURE__*/React.createElement(PAHeading, {
      sx: {
        marginBottom: '$spacing.xs'
      }
    }, "Thank you!"), /*#__PURE__*/React.createElement(Paragraph, {
      textAlign: "center",
      fontType: "bodySmallHeading",
      marginBottom: "lg"
    }, "We'll contact you in the next 1-2 business days."), /*#__PURE__*/React.createElement(Paragraph, {
      fontType: "bodySmall",
      textAlign: "center",
      marginBottom: "md"
    }, "In the meantime, explore any of the following next steps:"), /*#__PURE__*/React.createElement(Grid, {
      display: "grid",
      gridTemplateColumns: {
        default: '1fr',
        md: 'repeat(3, 1fr)'
      },
      gap: "sm",
      marginTop: "sm",
      marginBottom: "lg"
    }, /*#__PURE__*/React.createElement(Tile, {
      title: "Create your Zillow Premier Agent profile",
      IconOutline: IconNewTeamMemberOutline,
      onClick: function onClick() {
        trackTileLink('Create your Zillow Premier Agent profile', 'https://www.zillow.com/myzillow/Profile.htm');
      },
      href: "https://www.zillow.com/myzillow/Profile.htm",
      target: "_blank",
      fontType: "body"
    }), /*#__PURE__*/React.createElement(Tile, {
      title: "Download the Zillow Premier Agent app",
      IconOutline: IconDownloadOutline,
      onClick: function onClick() {
        trackTileLink('Download the Zillow Premier Agent app', 'https://premieragent.zillow.com/products/app/');
      },
      href: "https://premieragent.zillow.com/products/app/",
      target: "_blank",
      fontType: "body"
    }), /*#__PURE__*/React.createElement(Tile, {
      title: "Access training and resources",
      IconOutline: IconSchoolOutline,
      onClick: function onClick() {
        trackTileLink('Access training and resources', 'https://www.zillow.com/agent-resources/');
      },
      href: "https://www.zillow.com/agent-resources/",
      target: "_blank",
      fontType: "body"
    })), /*#__PURE__*/React.createElement(Paragraph, {
      textAlign: "center"
    }, "or call us directly at", ' ', /*#__PURE__*/React.createElement(Anchor, {
      href: "tel:8558850115"
    }, "855-885-0115"))))),
    renderFooter: /*#__PURE__*/React.createElement(Box, {
      as: Paragraph,
      fontType: "finePrint",
      textAlign: "center",
      sx: {
        width: '100%'
      }
    }, "We respect your privacy. See our", ' ', /*#__PURE__*/React.createElement(Anchor, {
      href: "https://www.zillowgroup.com/zg-privacy-policy/",
      target: "_blank"
    }, "Privacy Policy"))
  });
}
var PATipAlert = function PATipAlert(_ref) {
  var message = _ref.message;
  return /*#__PURE__*/React.createElement(Alert, {
    icon: /*#__PURE__*/React.createElement(IconBulbOutline, null),
    body: /*#__PURE__*/React.createElement(Paragraph, {
      fontColor: "backgroundDarkBlue"
    }, /*#__PURE__*/React.createElement("strong", null, "Did you know? "), message),
    role: "alert"
  });
};
var PAHeading = function PAHeading(props) {
  return /*#__PURE__*/React.createElement(Box, {
    as: Paragraph,
    fontType: {
      default: 'h4',
      md: 'h3'
    },
    fontFamily: "serif",
    textAlign: "center",
    marginBottom: "md",
    sx: Object.assign({
      marginBottom: '$spacing.md'
    }, props.sx),
    role: "heading"
  }, props.children);
};
var MobileBackButton = function MobileBackButton(props) {
  return /*#__PURE__*/React.createElement(Box, {
    sx: Object.assign({
      marginBottom: '$spacing.lg',
      textAlign: 'center'
    }, props.sx)
  }, /*#__PURE__*/React.createElement(TextButton, {
    type: "button",
    onClick: props.onClick
  }, "Go back"));
};

export { PAFormStepperModal as default };
